/**
 * `<caos-relationship-list>` — what points at this object, through which field, and what happens when it goes.
 * (Catalog: Developer surfaces › RelationshipList — an object's child relationships, with the field behind each and what a delete does to it.)
 *
 * A list of relationship names is almost useless, and that is what most tools show. The three things
 * a person actually needs when they are looking at this screen are: which OBJECT points here, through
 * which FIELD, and what happens to those records IF THIS ONE IS DELETED. The third is the reason
 * anybody opens the screen at all, and it is the one most often missing.
 *
 * DELETE BEHAVIOUR IS SPELLED OUT IN WORDS, not left as a vocabulary word. "cascade" is a word a
 * developer knows and a word that does not say what it does; the row reads "Deleting this record
 * deletes them" beside it. The distinction between cascade and restrict is the difference between
 * losing four thousand records and being told you cannot, so it is not a place to save eight
 * characters.
 *
 * AN UNKNOWN BEHAVIOUR IS SAID, NOT GUESSED. A relationship whose delete behaviour the caller does
 * not know reads "not stated" rather than defaulting to the safe-sounding one. Printing "restrict"
 * over a relationship nobody checked is how somebody deletes a record believing they were protected.
 *
 * THE COUNT IS OPTIONAL AND ITS ABSENCE IS VISIBLE. Counting child records costs a query per
 * relationship, so a surface may show the list long before it has the numbers, or never get them.
 * "not counted" and "0 records" are different facts and this list never conflates them — the same
 * rule `<caos-coverage-list>` and `<caos-object-field-explorer>` follow.
 *
 * Styling seams: `::part(list)`, `::part(summary)`, `::part(table)`, `::part(object-link)`,
 * `::part(behaviour)`, `::part(count)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** What happens to the children when the parent record is deleted. */
export type CaosDeleteBehaviour = 'cascade' | 'restrict' | 'clear';

/** What each behaviour actually DOES, in a sentence. See the header for why this is not optional. */
export const DELETE_BEHAVIOUR_MEANING: Readonly<Record<CaosDeleteBehaviour, string>> = {
  cascade: 'Deleting this record deletes them',
  restrict: 'This record cannot be deleted while they exist',
  clear: 'Deleting this record leaves them, with the reference emptied',
};

/** One relationship pointing at this object. */
export interface CaosRelationship {
  /** The relationship name — how it is addressed in a query. */
  readonly name: string;
  /** The object on the other end. */
  readonly childObject: string;
  /** The field on that object holding the reference. */
  readonly field: string;
  /** What a delete does. ABSENT ⇒ not stated, which is never rendered as a guess. */
  readonly onDelete?: CaosDeleteBehaviour;
  /** How many child records there are. ABSENT ⇒ not counted, which is not the same as zero. */
  readonly count?: number;
}

const sheet = makeSheet(`
  :host { display: block; }

  .list { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .summary {
    font-family: var(--caos-font);
    font-size: 12.5px;
    color: var(--caos-color-text-muted);
  }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }
  .summary .risk { color: var(--caos-color-danger); font-weight: 600; }

  .empty {
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

/** The relationships that would take records with them — what a delete warning is built from. */
export function cascadingOf(relationships: readonly CaosRelationship[]): readonly CaosRelationship[] {
  return relationships.filter((r) => r.onDelete === 'cascade');
}

export class CaosRelationshipList extends CaosElement {
  static get observedAttributes(): string[] {
    return ['sort-key', 'sort-direction', 'empty-text', 'linkable'];
  }

  private summaryEl: HTMLParagraphElement;
  private tableEl: CaosDataTable;
  private emptyEl: HTMLParagraphElement;

  private data: readonly CaosRelationship[] = [];

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'list';
    wrap.setAttribute('part', 'list');

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.hidden = true;

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });

    wrap.append(this.summaryEl, this.emptyEl, this.tableEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  get relationships(): readonly CaosRelationship[] {
    return this.data;
  }

  set relationships(next: readonly CaosRelationship[]) {
    this.data = Array.isArray(next) ? next : [];
    this.render();
  }

  /** The relationships that would take records with them if this record were deleted. */
  get cascading(): readonly CaosRelationship[] {
    return cascadingOf(this.data);
  }

  /** The relationships in the order they are shown. */
  get ordered(): readonly CaosRelationship[] {
    const key = this.getAttribute('sort-key') ?? 'childObject';
    const sign = this.getAttribute('sort-direction') === 'desc' ? -1 : 1;
    return [...this.data].sort((a, b) => {
      if (key === 'count') {
        // Uncounted rows sort to the end in both directions. They are not a count of zero.
        const x = a.count;
        const y = b.count;
        if (x === undefined && y === undefined) return a.name.localeCompare(b.name);
        if (x === undefined) return 1;
        if (y === undefined) return -1;
        if (x !== y) return (x - y) * sign;
        return a.name.localeCompare(b.name);
      }
      const read = (r: CaosRelationship): string => String((r as unknown as Record<string, unknown>)[key] ?? '');
      const cmp = read(a).localeCompare(read(b), undefined, { numeric: true, sensitivity: 'base' });
      return (cmp !== 0 ? cmp : a.name.localeCompare(b.name)) * sign;
    });
  }

  private openObject(name: string): void {
    this.dispatchEvent(new CustomEvent('caos-relationship-open', { bubbles: true, composed: true, detail: { object: name } }));
  }

  private render(): void {
    const empty = this.data.length === 0;
    this.emptyEl.hidden = !empty;
    this.tableEl.hidden = empty;
    this.emptyEl.textContent = this.getAttribute('empty-text') ?? 'Nothing points at this object.';

    if (empty) {
      // "Nothing points at this object" is a real and useful answer, so it is stated rather than
      // being left as a blank table somebody reads as a screen that failed to load.
      this.summaryEl.replaceChildren();
      return;
    }

    this.tableEl.columns = [
      { key: 'childObject', label: 'Object', sortable: true, width: '22%' },
      { key: 'name', label: 'Relationship', sortable: true, width: '22%' },
      { key: 'field', label: 'Through field', sortable: true, width: '20%' },
      { key: 'onDelete', label: 'On delete', sortable: true },
      { key: 'count', label: 'Records', sortable: true, align: 'end', width: '14%' },
    ];
    this.tableEl.sort = {
      key: this.getAttribute('sort-key') ?? 'childObject',
      direction: this.getAttribute('sort-direction') === 'desc' ? 'desc' : 'asc',
    };
    this.tableEl.rows = this.ordered.map((r) => this.rowFor(r));

    const nodes: Node[] = [];
    const strong = document.createElement('strong');
    strong.textContent = `${this.data.length} ${this.data.length === 1 ? 'relationship' : 'relationships'}`;
    nodes.push(strong);
    const cascading = this.cascading.length;
    if (cascading > 0) {
      nodes.push(document.createTextNode(' · '));
      const risk = document.createElement('span');
      risk.className = 'risk';
      risk.textContent = `${cascading} would delete records with this one`;
      nodes.push(risk);
    }
    const unstated = this.data.filter((r) => r.onDelete === undefined).length;
    if (unstated > 0) nodes.push(document.createTextNode(` · ${unstated} not stated`));
    this.summaryEl.replaceChildren(...nodes);
  }

  private rowFor(r: CaosRelationship): CaosTableRow {
    let objectCell: unknown = r.childObject;
    if (this.hasAttribute('linkable')) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'caos-table__idlink';
      b.setAttribute('part', 'object-link');
      b.textContent = r.childObject;
      b.setAttribute('aria-label', `Open ${r.childObject}`);
      b.addEventListener('click', () => this.openObject(r.childObject));
      objectCell = { node: b };
    }

    const behaviour = document.createElement('span');
    behaviour.setAttribute('part', 'behaviour');
    if (r.onDelete === undefined) {
      // Never guessed. Printing "restrict" over a relationship nobody checked is how somebody
      // deletes a record believing they were protected.
      behaviour.className = 'caos-table__cell-note';
      behaviour.textContent = 'not stated';
    } else {
      behaviour.textContent = `${r.onDelete} — ${DELETE_BEHAVIOUR_MEANING[r.onDelete]}`;
    }

    const count = document.createElement('span');
    count.setAttribute('part', 'count');
    if (r.count === undefined) {
      count.className = 'caos-table__cell-note';
      count.textContent = 'not counted';
    } else {
      count.textContent = r.count.toLocaleString();
    }

    return {
      id: r.name,
      cells: {
        childObject: objectCell,
        name: r.name,
        field: r.field,
        onDelete: { node: behaviour },
        count: { node: count },
      },
    } as CaosTableRow;
  }
}

define('caos-relationship-list', CaosRelationshipList);

declare global {
  interface HTMLElementTagNameMap {
    'caos-relationship-list': CaosRelationshipList;
  }
}
